import type { TrainingFollowUpDef } from '@hcc/types';
import { SHARED_INPUT_CLASS } from './pageMeta';

/** A choice page earlier in the deck that a follow-up may look back over. */
export interface FollowUpSourceOption {
  id: string;
  label: string;
}

/**
 * Editor for a follow-up page. The page has no questions of its own: it
 * names the answer to look for (usually "Yes") and, optionally, which
 * earlier pages to search. Everything else is presentation.
 */
export function FollowUpEditor({
  page,
  onChange,
  sources,
}: {
  page: TrainingFollowUpDef;
  onChange: (next: TrainingFollowUpDef) => void;
  /** Choice pages before this one, in deck order. */
  sources: FollowUpSourceOption[];
}) {
  const chosen = new Set(page.source_page_ids ?? []);
  const allEarlier = chosen.size === 0;

  function toggleSource(id: string, on: boolean) {
    const next = new Set(chosen);
    if (on) next.add(id);
    else next.delete(id);
    const ids = sources.map((s) => s.id).filter((s) => next.has(s));
    onChange({
      ...page,
      ...(ids.length > 0 ? { source_page_ids: ids } : { source_page_ids: undefined }),
    });
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Page heading</span>
        <input
          value={page.title ?? ''}
          onChange={(e) => onChange({ ...page, title: e.target.value })}
          placeholder="e.g. Tell us more"
          className={SHARED_INPUT_CLASS}
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Intro (optional)</span>
        <textarea
          rows={2}
          value={page.intro ?? ''}
          onChange={(e) => onChange({ ...page, intro: e.target.value })}
          placeholder='e.g. For each question you answered "Yes" to, please give some detail.'
          className={SHARED_INPUT_CLASS}
        />
      </label>

      <div className="flex flex-wrap items-end gap-3">
        <label className="w-48 text-sm">
          <span className="font-medium text-fg-muted">Answer to look for</span>
          <input
            value={page.match_label}
            onChange={(e) => onChange({ ...page, match_label: e.target.value })}
            placeholder="Yes"
            className={SHARED_INPUT_CLASS}
          />
        </label>
        <p className="pb-2 text-xs text-fg-dim">
          Matched against the option text the learner chose, ignoring case. True/false questions
          match &ldquo;True&rdquo; or &ldquo;False&rdquo;.
        </p>
      </div>

      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium text-fg-muted">Pages to look back over</legend>
        {sources.length === 0 ? (
          <p className="text-xs text-fg-dim">
            No choice pages come before this one yet. Add your Yes/No questions above it.
          </p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={allEarlier}
                onChange={(e) => {
                  if (e.target.checked) onChange({ ...page, source_page_ids: undefined });
                }}
              />
              Every choice page before this one
            </label>
            <div className="space-y-1 pl-5">
              {sources.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm text-fg-muted">
                  <input
                    type="checkbox"
                    checked={chosen.has(s.id)}
                    onChange={(e) => toggleSource(s.id, e.target.checked)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </>
        )}
      </fieldset>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 pb-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={page.multiline !== false}
            onChange={(e) => onChange({ ...page, multiline: e.target.checked })}
          />
          Large comments box
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={page.optional === true}
            onChange={(e) => onChange({ ...page, optional: e.target.checked })}
          />
          <span title="The learner can move on without explaining every match">
            Explanations optional
          </span>
        </label>
        <label className="min-w-56 flex-1 text-sm">
          <span className="font-medium text-fg-muted">Placeholder (optional)</span>
          <input
            value={page.placeholder ?? ''}
            onChange={(e) => onChange({ ...page, placeholder: e.target.value })}
            placeholder="e.g. Tell us a little more"
            className={SHARED_INPUT_CLASS}
          />
        </label>
      </div>
    </div>
  );
}
